import { NotificationService, TrackingEventType, useAnalytics, useIntl } from '@capsicum/common';
import {
	type ItemTabProps,
	Notification,
	TabBar,
	TABBAR_HEIGHT,
	useRouter,
	useSearchParam
} from '@capsicum/common-web';
import { useWalletFrontPageStickyOffset } from '@capsicum/features/nc-wallet/web';
import { ApprovedFooter } from '@capsicum/features/regulations/web';
import { TabNames, useWalletNotificationAtom } from '@capsicum/features/wallet/shared';
import dynamic from 'next/dynamic';
import { rgba } from 'polished';
import { useEffect } from 'react';
import styled from 'styled-components';

import { useWalletScroll } from '../../hooks/wallet/useScroll';

const LazyFanTokens = dynamic(() => import('../fan-tokens/FanTokens'));
const LazyDigitalCollectibles = dynamic(() => import('../digital-collectibles/DigitalCollectibles'));
const LazyMarket = dynamic(() => import('../market/Market'));

export const WalletContent = () => {
	const { $t } = useIntl();
	const analytics = useAnalytics();
	const { shallowReplaceSearchParam } = useRouter();
	const selectedTab = (useSearchParam('tab') || TabNames.FAN_TOKENS) as TabNames;

	const { scrollToTop } = useWalletScroll();

	const { setWalletNotification, walletNotification } = useWalletNotificationAtom();

	useEffect(() => {
		if (walletNotification) {
			NotificationService.addNotification({
				component: (
					<Notification
						message={walletNotification.description}
						title={walletNotification.title}
						variant={walletNotification.type}
					/>
				)
			});

			setWalletNotification(undefined);
		}
	}, [walletNotification, setWalletNotification]);

	const handleTabChange = async (tab: string) => {
		if (tab === TabNames.DIGITAL_COLLECTIBLES) {
			analytics.sendEvent(TrackingEventType.track, 'WalletDigitalCollectibleTabClick');
		}

		if (selectedTab !== tab) {
			shallowReplaceSearchParam('tab', tab);
			await scrollToTop();
		}
	};

	const stickyOffsetTop = useWalletFrontPageStickyOffset();

	const walletFeatures: Array<ItemTabProps> = [
		{
			component: LazyFanTokens,
			hasActivityIndicator: false,
			id: TabNames.FAN_TOKENS,
			tabTestID: 'wallet-contentTabs-fanTokens',
			text: $t('wallet.fanTokens.title')
		},
		{
			component: LazyDigitalCollectibles,
			hasActivityIndicator: false,
			id: TabNames.DIGITAL_COLLECTIBLES,
			tabTestID: 'wallet-contentTabs-digitalCollectibles',
			text: $t('wallet.digitalCollectibles.title')
		},
		{
			component: () => <LazyMarket stickyOffsetTop={stickyOffsetTop + TABBAR_HEIGHT} />,
			hasActivityIndicator: false,
			id: TabNames.MARKET,
			tabTestID: 'wallet-contentTabs-market',
			text: $t('wallet.market.title')
		}
	];

	return (
		<S.Container>
			<S.TabsContainer>
				<S.Tabs
					items={walletFeatures}
					preserveState={false}
					selected={selectedTab}
					stickyOffsetTop={stickyOffsetTop}
					testID="wallet-tabBar"
					variant="fixed"
					onChangeSelected={handleTabChange}
				/>
				<ApprovedFooter page="wallet" />
			</S.TabsContainer>
		</S.Container>
	);
};

namespace S {
	export const Container = styled.main`
		display: flex;
		flex-flow: column;
		height: 100%;
		width: 100%;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		min-height: 100vh;
	`;
	export const TabsContainer = styled.section`
		background-color: ${({ theme }) => theme.colors.backgrounds.main.screen};
		flex-grow: 1;
	`;
	export const Tabs = styled(TabBar)`
		background-color: ${({ theme }) => rgba(theme.colors.backgrounds.main.screen, 0.9)};
	`;
}
